import { Injectable, inject } from '@angular/core';
import { Amendment } from '@app/features/amendments/amendment.model';
import { ContractListItem } from '@app/features/contracts/contract.model';
import { Observable, map } from 'rxjs';
import { AMENDMENT_ORIGIN, ContractAmendmentsHistory } from './contract-amendments-history';
import {
  ContractHistorySource,
  HistoryEntry,
  HistorySubject,
  HistoryText,
} from './history-entry.model';
import { validationEntries } from './validation-history-source';

export { AMENDMENT_ORIGIN } from './contract-amendments-history';

function abandonTitle(amendment: Amendment): HistoryText {
  const by = amendment.abandonedBy ? [{ userId: amendment.abandonedBy }] : [];
  switch (amendment.abandonCause) {
    case 'CONTRACT_CANCELED':
      return ["Avenant abandonné avec l'annulation du contrat"];
    case 'CONTRACT_DELETED':
      return by.length
        ? ['Avenant abandonné avec la suppression du contrat par ', ...by]
        : ['Avenant abandonné avec la suppression du contrat'];
    case 'ABANDONED_IN_SIGNING':
      return ['Avenant abandonné en signature par ', ...by];
    case 'SIGNATURE_CANCELED':
      return ['Avenant abandonné : sa demande de signature a été annulée chez Yousign'];
    default:
      return by.length ? ['Avenant abandonné par ', ...by] : ['Avenant abandonné'];
  }
}

/**
 * Les avenants du contrat : l'établissement, l'abandon et la signature de chacun, et les gestes de
 * leurs demandes de validation, mêlés au fil de l'embauche et marqués de leur avenant.
 */
@Injectable()
export class AmendmentHistorySource implements ContractHistorySource {
  readonly origin = AMENDMENT_ORIGIN;

  private readonly amendments = inject(ContractAmendmentsHistory);

  entries(contract: ContractListItem): Observable<HistoryEntry[]> {
    return this.amendments
      .of(contract)
      .pipe(
        map(({ amendments, subjectOf }) =>
          amendments.flatMap((amendment) => this.facts(amendment, subjectOf(amendment.id)!)),
        ),
      );
  }

  private facts(amendment: Amendment, subject: HistorySubject): HistoryEntry[] {
    const entry = (fact: string, at: string, title: HistoryText): HistoryEntry => ({
      id: `avenant-${amendment.id}-${fact}`,
      origin: AMENDMENT_ORIGIN,
      subject,
      at,
      title,
    });
    const entries: HistoryEntry[] = [
      {
        ...entry('etabli', amendment.createdAt, [
          'Avenant établi par ',
          { userId: amendment.createdBy },
        ]),
        lines: amendment.reasons.length
          ? [{ text: [amendment.reasons.map((reason) => reason.label).join(', ')] }]
          : [],
      },
    ];
    if (amendment.status === 'CANCELED' && amendment.abandonedAt) {
      entries.push({
        ...entry('abandonne', amendment.abandonedAt, abandonTitle(amendment)),
        ...(amendment.abandonComment ? { lines: [{ text: [amendment.abandonComment] }] } : {}),
      });
    }
    if (amendment.status === 'SIGNED' && amendment.signedAt) {
      entries.push(
        entry(
          'signe',
          amendment.signedAt,
          amendment.signedBy
            ? ['Avenant signé : exemplaire déposé par ', { userId: amendment.signedBy }]
            : ['Avenant signé par Yousign'],
        ),
      );
    }
    return [...entries, ...validationEntries(amendment.validationRequests ?? [], subject)];
  }
}
